import { RefObject, useCallback, useContext, useEffect, useRef, useState, useReducer } from 'react';

import { PlayerContext } from './PlayerContext';
import { PlayerType } from './PlayerLoader';

type VideoPlayer = {
  loop: boolean;
  quality: string;
  volume: number;
  videoId?: number;
  unload: () => void;
  destroy: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function usePlayer<T = any>(
  eleRef: RefObject<HTMLElement | null>,
  clipId: number,
  initNow: boolean,
  clipConfigUrl: string,
  loop = true,
  quality = '540p',
  volume = 1,
  delegates?: Record<string, unknown>,
): { player: T; flushPlayer: () => void; forceReload: () => void } {
  const { PlayerConstructor, playerType } = useContext(PlayerContext);
  const isVideoRequested = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [player, setPlayer] = useState<any>(null);
  const playerInstance = useRef<VideoPlayer | null>(null);

  // Needed to update player value after flush method call, analog for forceUpdate method
  // https://legacy.reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [reloadTrigger, forceUpdate] = useReducer((x) => x + 1, 0);

  const configurePlayer = useCallback(() => {
    const configureBarebonePlayer = () => {
      const playerReady = new PlayerConstructor(eleRef.current, clipConfigUrl);
      playerReady
        .then((videoPlayer: VideoPlayer) => {
          videoPlayer.loop = loop;
          videoPlayer.quality = quality;
          videoPlayer.volume = volume;
          setPlayer(videoPlayer);
          playerInstance.current = videoPlayer;
          isVideoRequested.current = false;
        })
        .catch(() => {
          isVideoRequested.current = false;
        });
    };

    isVideoRequested.current = true;

    if (playerType === PlayerType.BarebonePlayer) {
      configureBarebonePlayer();
    } else {
      const playerReady = new PlayerConstructor(
        eleRef.current,
        clipConfigUrl,
        true,
        delegates || {},
      );
      setPlayer(playerReady);
      playerInstance.current = playerReady;
      isVideoRequested.current = false;
    }
  }, [PlayerConstructor, eleRef, clipConfigUrl, playerType, loop, quality, volume, delegates]);

  // flushPlayer resets the player to it's default state and
  // sets that state to null so we trigger the useEffect below.
  const flushPlayer = useCallback(() => {
    if (playerInstance.current) {
      playerInstance.current.unload?.();
      setPlayer(null);
      playerInstance.current = null;
    }
  }, []);

  // destroyPlayer strips the player of its video
  // so that the media cannot be inadvertently played after unmount
  const destroyPlayer = () => {
    if (playerInstance.current) {
      playerInstance.current.destroy?.();
      setPlayer(null);
      playerInstance.current = null;
    }
  };

  // flush and then force update player with updated config
  // it allows to update the list of captions language
  const forceReload = useCallback(() => {
    flushPlayer();
    forceUpdate();
  }, [flushPlayer]);

  useEffect(
    function configure() {
      if (player?.videoId === Number(clipId)) {
        return;
      }

      if (playerInstance.current && player && clipConfigUrl) {
        return;
      }

      if (
        PlayerConstructor &&
        initNow &&
        eleRef.current &&
        clipId &&
        clipConfigUrl &&
        !isVideoRequested.current
      ) {
        // BarebonePlayer creates a video element inside the ref element even if the
        // video api call fails. To remove the video element before making another
        // call, this line of code is used.
        eleRef.current.innerHTML = '';

        // We want to configure player once so timeout is used to workaround React Strict Mode
        const timerId = setTimeout(configurePlayer, 0);

        return () => {
          clearTimeout(timerId);
        };
      }
    },
    [
      PlayerConstructor,
      player,
      player?.videoId,
      reloadTrigger,
      eleRef,
      clipId,
      clipConfigUrl,
      initNow,
      loop,
      quality,
      configurePlayer,
    ],
  );

  // Destroy the player on unmount so that the video
  // cannot be unintentionally played when out of the DOM
  useEffect(function destroy() {
    return () => {
      destroyPlayer();
    };
  }, []);

  return { player, flushPlayer, forceReload };
}
